import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Compensation, sumCompensation } from '@/domain/refund'

const AMOUNT_FORMAT = /^\d+(?:[.]\d+)?$/

interface RefundAmountEditorOptions {
  compensation: Compensation
  maximumRefundAmount?: string
  isReverLite: boolean
  // Taken off the committed refund before prefilling, e.g. declined items.
  deduction?: number
}

export function useRefundAmountEditor({
  compensation,
  maximumRefundAmount,
  isReverLite,
  deduction = 0
}: RefundAmountEditorOptions) {
  const { t } = useTranslation()
  const { currency, amount: committed } = sumCompensation(
    compensation,
    'adjustedCommitment'
  )
  const calculatedRefundAmount = Math.max(Number(committed) - deduction, 0)

  const [refundAmount, setRefundAmount] = useState(
    calculatedRefundAmount.toFixed(2)
  )

  // A prefill left behind by refreshed data would read as an edit.
  useEffect(() => {
    setRefundAmount(calculatedRefundAmount.toFixed(2))
  }, [calculatedRefundAmount])

  const formatError = !AMOUNT_FORMAT.test(refundAmount)
  const amountError =
    !isReverLite &&
    parseFloat(refundAmount) > parseFloat(maximumRefundAmount ?? '0')
  const differenceRefundAmount =
    parseFloat(calculatedRefundAmount.toFixed(2)) - parseFloat(refundAmount)

  const hint = () => {
    if (formatError) return t('refund_island.valid_amount')
    if (amountError) return t('refund_island.exceeds_amount')
    if (differenceRefundAmount === 0) return ''
    const direction =
      differenceRefundAmount < 0
        ? t('refund_island.more')
        : t('refund_island.less')
    return `${t('refund_island.refunding')} ${Math.abs(
      differenceRefundAmount
    ).toFixed(2)} ${direction}`
  }

  return {
    currency,
    calculatedRefundAmount,
    refundAmount,
    setRefundAmount,
    differenceRefundAmount,
    amountEdited: differenceRefundAmount !== 0,
    error: formatError || amountError,
    hint: hint()
  }
}
